import { useState, useEffect } from 'react';
import { Download, FileText, Calendar, AlertCircle, CheckCircle, Clock, XCircle, Filter, DollarSign } from 'lucide-react';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';

interface PaymentHistoryItem {
  paymentId: number;
  amount: number;
  dueDate: string;
  status: string;
  method: string;
  propertyAddress: string;
  transactionId?: string;
  receiptId?: string;
  isLate?: boolean;
  lateFee?: number;
  createdAt: string;
}

interface AnnualSummary {
  year: number;
  totalPaid: number;
  totalDue: number;
  paidCount: number;
  overdueCount: number;
  lateFees: number;
  monthlyBreakdown: { [key: number]: number };
}

export default function PaymentHistory() {
  const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<PaymentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [yearFilter, setYearFilter] = useState<number | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  // Annual summary
  const [showAnnualSummary, setShowAnnualSummary] = useState(false);
  const [annualSummary, setAnnualSummary] = useState<AnnualSummary | null>(null);

  useEffect(() => {
    loadPaymentHistory();
    loadAvailableYears();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [payments, statusFilter, yearFilter]);

  const loadPaymentHistory = async () => {
    try {
      const response = await api.get<{payments: PaymentHistoryItem[]}>('/renter/payment-history?size=1000');
      setPayments(response.payments || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableYears = async () => {
    try {
      const response = await api.get<{years: number[]}>('/renter/payment-history/years');
      setAvailableYears(response.years || []);
    } catch (err) {
      console.error('Failed to load years:', err);
    }
  };

  const loadAnnualSummary = async (year: number) => {
    try {
      const response = await api.get<AnnualSummary>(`/renter/payment-history/annual-summary/${year}`);
      setAnnualSummary(response);
      setShowAnnualSummary(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load annual summary');
    }
  };

  const applyFilters = () => {
    let filtered = [...payments];

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    if (yearFilter) {
      filtered = filtered.filter(p => new Date(p.dueDate).getFullYear() === yearFilter);
    }

    setFilteredPayments(filtered);
  };

  const exportCSV = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8787/api'}/ledger/export/csv`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rentry_access')}`
        }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rent-ledger.csv';
      a.click();
    } catch (err: any) {
      alert('Failed to export CSV: ' + err.message);
    }
  };

  const exportPDF = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8787/api'}/ledger/export/pdf`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rentry_access')}`
        }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rent-ledger.pdf';
      a.click();
    } catch (err: any) {
      alert('Failed to export PDF: ' + err.message);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PAID':
        return <CheckCircle className="w-5 h-5" style={{ color: '#49c5b6' }} />;
      case 'SCHEDULED':
        return <Clock className="w-5 h-5" style={{ color: '#0C4432' }} />;
      case 'FAILED':
      case 'OVERDUE':
        return <AlertCircle className="w-5 h-5" style={{ color: '#DF6C4F' }} />;
      default:
        return <XCircle className="w-5 h-5" style={{ color: '#666' }} />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PAID': return 'Paid';
      case 'SCHEDULED': return 'Scheduled';
      case 'FAILED': return 'Failed';
      case 'OVERDUE': return 'Overdue';
      default: return status;
    }
  };

  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#0C4432' }}></div>
      </div>
    );
  }

  const totalPaid = filteredPayments
    .filter(p => p.status === 'PAID')
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#F8F8F8' }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <PageHeader
          title="Payment History"
          subtitle="Track all your rent payments and receipts"
          icon={<DollarSign className="w-8 h-8" />}
          backPath="/dashboard"
        >
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 px-4 py-2 rounded-md border font-medium hover:bg-gray-50 border-secondary text-secondary"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={exportPDF}
            className="flex items-center gap-2 px-4 py-2 rounded-md border font-medium hover:bg-gray-50 border-primary text-primary"
          >
            <FileText className="w-4 h-4" />
            Export PDF
          </button>
        </PageHeader>

        {error && (
          <div className="mb-6 p-4 rounded-md" style={{ backgroundColor: '#DF6C4F', color: '#F8F8F8' }}>
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm" style={{ color: '#666' }}>Total Paid</p>
            <p className="text-2xl font-bold" style={{ color: '#0C4432' }}>
              {formatCurrency(totalPaid)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm" style={{ color: '#666' }}>Total Payments</p>
            <p className="text-2xl font-bold" style={{ color: '#0C4432' }}>
              {filteredPayments.length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm" style={{ color: '#666' }}>Pending</p>
            <p className="text-2xl font-bold" style={{ color: '#666' }}>
              {filteredPayments.filter(p => p.status === 'SCHEDULED').length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm" style={{ color: '#666' }}>Failed</p>
            <p className="text-2xl font-bold" style={{ color: '#DF6C4F' }}>
              {filteredPayments.filter(p => p.status === 'FAILED').length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5" style={{ color: '#666' }} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="ALL">All Statuses</option>
              <option value="PAID">Paid</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="FAILED">Failed</option>
              <option value="OVERDUE">Overdue</option>
            </select>

            <select
              value={yearFilter || ''}
              onChange={(e) => setYearFilter(e.target.value ? Number(e.target.value) : null)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="">All Years</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>

            {availableYears.length > 0 && (
              <button
                onClick={() => loadAnnualSummary(yearFilter || availableYears[0])}
                className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium"
                style={{ backgroundColor: '#49c5b6' }}
              >
                <Calendar className="w-4 h-4" />
                Annual Summary
              </button>
            )}
          </div>
        </div>

        {/* Annual Summary Modal */}
        {showAnnualSummary && annualSummary && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold" style={{ color: '#0C4432' }}>
                  {annualSummary.year} Tax Summary
                </h2>
                <button
                  onClick={() => setShowAnnualSummary(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 rounded-md" style={{ backgroundColor: '#E8F5F3' }}>
                  <p className="text-sm" style={{ color: '#666' }}>Total Paid</p>
                  <p className="text-xl font-bold" style={{ color: '#0C4432' }}>
                    {formatCurrency(annualSummary.totalPaid)}
                  </p>
                </div>
                <div className="p-4 rounded-md" style={{ backgroundColor: '#F8F8F8' }}>
                  <p className="text-sm" style={{ color: '#666' }}>Payments Made</p>
                  <p className="text-xl font-bold" style={{ color: '#0C4432' }}>
                    {annualSummary.paidCount}
                  </p>
                </div>
                <div className="p-4 rounded-md" style={{ backgroundColor: '#FEE' }}>
                  <p className="text-sm" style={{ color: '#666' }}>Late Fees</p>
                  <p className="text-xl font-bold" style={{ color: '#DF6C4F' }}>
                    {formatCurrency(annualSummary.lateFees)}
                  </p>
                </div>
                <div className="p-4 rounded-md" style={{ backgroundColor: '#F8F8F8' }}>
                  <p className="text-sm" style={{ color: '#666' }}>Overdue Count</p>
                  <p className="text-xl font-bold" style={{ color: '#DF6C4F' }}>
                    {annualSummary.overdueCount}
                  </p>
                </div>
              </div>

              <h3 className="font-semibold mb-3" style={{ color: '#0C4432' }}>Monthly Breakdown</h3>
              <div className="space-y-2">
                {Object.entries(annualSummary.monthlyBreakdown)
                  .sort(([a], [b]) => Number(a) - Number(b))
                  .map(([month, amount]) => (
                    <div key={month} className="flex justify-between items-center p-2 rounded" style={{ backgroundColor: '#F8F8F8' }}>
                      <span>{new Date(2025, Number(month) - 1).toLocaleDateString('en-US', { month: 'long' })}</span>
                      <span className="font-semibold">{formatCurrency(amount)}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Payment List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {filteredPayments.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: '#666' }} />
              <p style={{ color: '#666' }}>No payments found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{ backgroundColor: '#0C4432' }}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-white">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-white">
                      Property
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-white">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-white">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-white">
                      Receipt
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredPayments.map((payment) => (
                    <tr key={payment.paymentId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium" style={{ color: '#222' }}>
                          {formatDate(payment.dueDate)}
                        </div>
                        {payment.transactionId && (
                          <div className="text-xs" style={{ color: '#666' }}>
                            {payment.transactionId}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm" style={{ color: '#222' }}>
                          {payment.propertyAddress}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold" style={{ color: '#0C4432' }}>
                          {formatCurrency(payment.amount)}
                        </div>
                        {payment.isLate && payment.lateFee && (
                          <div className="text-xs" style={{ color: '#DF6C4F' }}>
                            +{formatCurrency(payment.lateFee)} late fee
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(payment.status)}
                          <span className="text-sm">{getStatusLabel(payment.status)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payment.receiptId && (
                          <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#E8F5F3', color: '#0C4432' }}>
                            Available
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

